import { API } from '../constants/apiEndpoints';
import { apiUrls } from '../utils/apiUtils';
import { axiosInstance } from '../utils/fetcher';
import { UnifiedTransactionPayload } from '../types/unified-transaction';

export const TransactionService = {
  /**
   * Create transaction using unified endpoint
   * Handles all transaction types: expense, income, transfer, borrow, lend, repay, collect
   */
  createUnifiedTransaction: async (payload: UnifiedTransactionPayload): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.CREATE, payload);
    return response.data;
  },

  /**
   * Update transaction using unified endpoint
   * Handles type changes automatically (deletes old, creates new)
   */
  updateUnifiedTransaction: async (id: string, payload: UnifiedTransactionPayload): Promise<any> => {
    const response = await axiosInstance.put(apiUrls.updateTransaction(id), payload);
    return response.data;
  },

  // Legacy methods (deprecated - use unified methods above)
  createExpenseTransaction: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.EXPENSE, payload);
    return response.data;
  },

  createIncomeTransaction: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.INCOME, payload);
    return response.data;
  },

  createTransferTransaction: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.TRANSFER, payload);
    return response.data;
  },

  createBorrowTransaction: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.CREATE_BORROWING, payload);
    return response.data;
  },

  createLendTransaction: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.CREATE_LENDING, payload);
    return response.data;
  },

  createRepayTransaction: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.CREATE_REPAY, payload);
    return response.data;
  },

  createCollectTransaction: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.TRANSACTIONS.CREATE_COLLECT, payload);
    return response.data;
  },
  
  getExpenseCategory: async (): Promise<any> => {
    const response = await axiosInstance.get(API.CATEGORIES.EXPENSE);
    return response.data;
  },

  getIncomeCategory: async (): Promise<any> => {
    const response = await axiosInstance.get(API.CATEGORIES.INCOME);
    return response.data;
  },

  getTransactionsDetail: async (id: string): Promise<any> => {
    const response = await axiosInstance.get(apiUrls.transactionById(id));
    return response.data;
  },

  deleteTransaction: async (id: string): Promise<any> => {
    const response = await axiosInstance.delete(apiUrls.transactionById(id));
    return response.data;
  },

  updateIncomeTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.UPDATE_INCOME_TRANSACTION.replace(':id', id), payload);
    return response.data;
  },

  updateExpenseTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.UPDATE_EXPENSE_TRANSACTION.replace(':id', id), payload);
    return response.data;
  },

  updateTransferTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.UPDATE_TRANSFER_TRANSACTION.replace(':id', id), payload);
    return response.data;
  },

  updateBorrowTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.BORROWING_BY_ID.replace(':transactionId', id), payload);
    return response.data;
  },

  updateBorrowExpenseTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.BORROW_EXPENSE_BY_ID.replace(':transactionId', id), payload);
    return response.data;
  },

  updateRepaymentTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.UPDATE_REPAY.replace(':transactionId', id), payload);
    return response.data;
  },

  updateLendTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.LENDING_BY_ID.replace(':transactionId', id), payload);
    return response.data;
  },

  updateCollectTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.UPDATE_COLLECT.replace(':id', id), payload);
    return response.data;
  },

  updateClearDebtTransaction: async (id: string, payload?: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.CLEAR_DEBT_BY_ID.replace(':transactionId', id), payload);
    return response.data;
  },

  updateTransaction: async (transactionId: string, payload: any): Promise<any> => {
    const response = await axiosInstance.put(API.TRANSACTIONS.UPDATE_TRANSACTION.replace(':transactionId', transactionId), payload);
    return response.data;
  }
};